import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import { Row, Col, Spin, Input, Button, Typography, Checkbox } from 'antd'
import { getClinicsService, createNewClinicService } from '../../redux/systemInfos/systemInfosServices';
import { DvIntlMessages, DvNotification } from '../../components/utility/dvGeneralUtilities';
import { DEBOUNCE_TIME_SHORT } from '../../helpers/utility';
import appActions from '../../redux/app/actions';

const { Title } = Typography;
const { redirectTo } = appActions;

class CreateNewClinic extends Component {
  state = {
    isLoadingClinics: true,
    isClinicBeingCreated: false
  };

  labelColumnStyle = { flex: '0 1 200px', color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 };
  valueColumnStyle = { flex: '1 1 300px', minHeight: 32, maxWidth: 400 };
  labelStyle = { margin: 0, minHeight: 32, display: 'flex', alignItems: 'center' };
  validationMessageStyle = { marginLeft: 12, color: '#f5222d', minHeight: 24 };
  existingTitles = [];
  existingEmails = [];

  componentDidMount() {
    // Get all existing clinics for new clinic's Title and Email singularity checks
    getClinicsService().then((response) => {
      if (response.status === 1 && response.results && response.results.clinics) {
        this.existingTitles = response.results.clinics.map(clinic => { return clinic.title });
        this.existingEmails = response.results.clinics.filter(clinic => clinic.oncoEmailAddress).map(clinic => { return clinic.oncoEmailAddress.replace('icmd+', '').replace('@oncorecords.com', '') });
      } else DvNotification({
        type: 'error',
        titleId: 'clinicsPage.createNewClinic',
        messageId: response.errors ? null : 'error.general.failToGetData',
        messageString: response.errors.join(', ')
      });

      this.setState({
        formFields: {
          clinicTitle: undefined,
          countryCode: undefined,
          clinicEmail: undefined,
          isEnterpriseProduct:false
        },
        formValidationMessages: { clinicTitleValidationMessage: undefined, countryCodeValidationMessage: undefined, clinicEmailValidationMessage: undefined },
        isLoadingClinics: false,
      }, () => Object.keys(this.state.formFields).forEach(key => this.validateField(key, this.state.formFields[key])));
    })
  }

  validateFieldDebounced = debounce((field, value) => {
    this.validateField(field, value);
  }, DEBOUNCE_TIME_SHORT);

  validateField = (field, value) => {
    let validationMessage = undefined;
    let formValidationMessagesField = field + 'ValidationMessage';
    const emailRegEx = /^\w+([.-]?\w+)*$/;

    switch (field) {
      case 'clinicTitle':
        if (!value || value.length === 0) validationMessage = DvIntlMessages('general.title') + DvIntlMessages('general.isRequired');
        else if (this.existingTitles.includes(value)) validationMessage = DvIntlMessages('error.titleIsTaken');
        else if (value && value.length > 255) validationMessage = DvIntlMessages('input.noLongerThan255');
        break;
      case 'countryCode':
        if (!value || value.length === 0) validationMessage = DvIntlMessages('general.countryCode') + DvIntlMessages('general.isRequired');
        else if (value && value.length !== 3) validationMessage = DvIntlMessages('input.exactly3');
        break;
      case 'clinicEmail':
        if (!value || value.length === 0) return;
        else if (value.length > 50) validationMessage = DvIntlMessages('input.noLongerThan50');
        else if (!emailRegEx.test(String(value).toLowerCase())) validationMessage = DvIntlMessages('error.invalidEmail');
        else if (this.existingEmails.includes(value)) validationMessage = DvIntlMessages('error.emailIsTaken');
        break;
      default:
        return;
    }

    this.setState(prevState => ({ formValidationMessages: { ...prevState.formValidationMessages, [formValidationMessagesField]: validationMessage } }));
  }

  hasFormErrors = () => {
    const { formValidationMessages } = this.state;
    if (formValidationMessages) {
      for (const key of Object.keys(formValidationMessages)) {
        if (formValidationMessages[key]) return true;
      }
    }

    return false;
  }

  createNewClinic = () => {
    this.setState({ isClinicBeingCreated: true }, () => {
      const valuesToStore = {
        title: this.state.formFields.clinicTitle,
        countryCode: this.state.formFields.countryCode,
        oncoEmailAddress: this.state.formFields.clinicEmail ? this.state.formFields.clinicEmail : null,
        isEnterpriseProduct: this.state.formFields.isEnterpriseProduct
      }

      createNewClinicService(valuesToStore).then((response) => {
        this.setState({ isClinicBeingCreated: false }, () => {
          if (response.status === 1) this.props.redirectTo('/Dashboard/clinics')
          else DvNotification({
            type: 'error',
            titleId: 'clinicsPage.createNewClinic',
            messageId: response.errors ? null : 'error.createNewClinic',
            messageString: response.errors.join(', ')
          });
        });
      });
    })
  }

  render() {
    if (!this.state.formValidationMessages || !this.state.formFields) return null;
    let { clinicTitle, countryCode, clinicEmail, isEnterpriseProduct } = this.state.formFields;
    const { clinicTitleValidationMessage, countryCodeValidationMessage, clinicEmailValidationMessage } = this.state.formValidationMessages;

    return <>
      <Spin spinning={this.state.isLoadingClinics || this.state.isClinicBeingCreated}>
        <Title level={4} style={{ marginBottom: 32 }}>
          {DvIntlMessages('clinicsPage.createNewClinic')}
        </Title>

        <div style={{ maxWidth: 600 }}>
          {/** clinicTitle */}
          <Row type='flex' align='top'>
            <Col style={this.labelColumnStyle}>
              <label style={this.labelStyle}>
                {DvIntlMessages('general.title')}:
              </label>
            </Col>
            <Col style={this.valueColumnStyle}>
              <Row>
                <Input type='text' placeholder={DvIntlMessages('general.title')} value={clinicTitle} maxLength={256}
                  style={{ borderColor: clinicTitleValidationMessage ? 'red' : null, boxShadow: clinicTitleValidationMessage ? '0 0 0 0' : null }}
                  onChange={(e) => {
                    const value = e.target.value;
                    this.setState(prevState => ({ formFields: { ...prevState.formFields, clinicTitle: value } }));
                    this.validateFieldDebounced('clinicTitle', value);
                  }} />
              </Row>
              <Row style={this.validationMessageStyle}>{clinicTitleValidationMessage}</Row>
            </Col>
          </Row>

          {/** countryCode */}
          <Row type='flex' align='top'>
            <Col style={this.labelColumnStyle}>
              <label style={this.labelStyle}>
                {DvIntlMessages('general.countryCode')}:
              </label>
            </Col>
            <Col style={this.valueColumnStyle}>
              <Row>
                <Input type='text' placeholder={DvIntlMessages('general.countryCode')} value={countryCode} maxLength={256}
                  style={{ borderColor: countryCodeValidationMessage ? 'red' : null, boxShadow: countryCodeValidationMessage ? '0 0 0 0' : null }}
                  onChange={(e) => {
                    const value = e.target.value;
                    this.setState(prevState => ({ formFields: { ...prevState.formFields, countryCode: value } }));
                    this.validateFieldDebounced('countryCode', value);
                  }} />
              </Row>
              <Row style={this.validationMessageStyle}>{countryCodeValidationMessage}</Row>
            </Col>
          </Row>

          {/** clinicEmail */}
          <Row type='flex' align='top'>
            <Col style={this.labelColumnStyle}>
              <label style={this.labelStyle}>
                {DvIntlMessages('general.email')}:
              </label>
            </Col>
            <Col style={this.valueColumnStyle}>
              <Row>
                <Col style={{ display: 'flex', alignItems: 'center' }}>icmd+</Col>
                <Col style={{ display: 'flex', flex: '1 1' }}>
                  <Input type='text' placeholder={DvIntlMessages('general.email')} value={clinicEmail} maxLength={256}
                    style={{ borderColor: clinicEmailValidationMessage ? 'red' : null, boxShadow: clinicEmailValidationMessage ? '0 0 0 0' : null }}
                    onChange={(e) => {
                      const value = e.target.value;
                      this.setState(prevState => ({ formFields: { ...prevState.formFields, clinicEmail: value } }));
                      this.validateFieldDebounced('clinicEmail', value);
                    }} />
                </Col>
                <Col style={{ display: 'flex', alignItems: 'center' }}>@oncorecords.com</Col>
              </Row>
              <Row style={this.validationMessageStyle}>{clinicEmailValidationMessage}</Row>
            </Col>
          </Row>

          {/** enterprise product */}
          <Row type='flex' align='top'>
            <Col style={this.labelColumnStyle}>
              <label style={this.labelStyle}>
                {DvIntlMessages('general.enterpriseProduct')}:
              </label>
            </Col>
            <Col style={this.valueColumnStyle}>
              <Row>
                <Checkbox checked={isEnterpriseProduct} onChange={(e)=>this.setState(prevState=>({formFields: { ...prevState.formFields, isEnterpriseProduct:e.target.checked }}))}/>
              </Row>
            </Col>
          </Row>

          <Row type='flex' justify='end'>
            <Button type='primary' disabled={this.hasFormErrors()} onClick={() => this.createNewClinic()}>
              {DvIntlMessages('general.save')}
            </Button>
            <Button type='danger' onClick={() => this.props.redirectTo('/Dashboard/clinics')} style={{ marginLeft: 10 }}>
              {DvIntlMessages('general.cancel')}
            </Button>
          </Row>
        </div>
      </Spin>
    </>;
  }
}

CreateNewClinic.propTypes = {
  redirectTo: PropTypes.func.isRequired
};

const mapActionsToProps = {
  redirectTo
};

function mapStateToProps(state) {
  return {
  };
}

const connectedClinicsPage = connect(mapStateToProps, mapActionsToProps)(CreateNewClinic);
export default connectedClinicsPage;